"use client";

import { useState, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
	Search,
	MapPin,
	Calendar,
	Building,
	Lock,
	CheckCircle,
	Sparkles,
	Database,
	DollarSign,
	Download,
	Home,
	Mail,
	User,
	FileText,
	MapPinned,
	Ruler,
	ChevronDown,
	ChevronUp,
	ArrowRight,
	Shield,
	TrendingUp,
	Clock,
	Navigation2,
	LogIn,
	Loader2,
	Camera,
	Copy,
	Check,
} from "lucide-react";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { isAuthenticatedSync } from "@/lib/auth";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import { toast } from "sonner";
import type { ScrapedParcelData } from "@/types/parcel";
import { ExternalLink as ExternalLinkIcon } from "lucide-react";
import RequestPhotosModal from "./RequestPhotosModal";
import Image from "next/image";
import { checkPropertyMediaRequest } from "@/services/property-media-api";
import { PhotoRequestAlreadySentBlock } from "./PhotoRequestAlreadySentBlock";

// Set Mapbox token
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN || "";
mapboxgl.accessToken = MAPBOX_TOKEN;

const MEDIA_BASE_URL =
	process.env.NEXT_PUBLIC_MEDIA_BASE_URL || "https://media.taxlienvest.com/";
// Court Case URL Mapping
const COURT_CASE_URLS: Record<string, Record<string, string>> = {
	"North Carolina": {
		Cabarrus: "https://portal-nc.tylertech.cloud/Portal/Home/Dashboard/29",
		// Add more counties here as needed
	},
	// Add more states here as needed
};

/**
 * Get the court case URL for a given county and state
 * Falls back to state-level URL if county-specific URL is not found
 */
function getCourtCaseUrl(county: string, state: string): string | null {
	const normalizedState = state.trim();
	const normalizedCounty = county.trim();

	// Check for county-specific URL
	if (
		COURT_CASE_URLS[normalizedState] &&
		COURT_CASE_URLS[normalizedState][normalizedCounty]
	) {
		return COURT_CASE_URLS[normalizedState][normalizedCounty];
	}

	// Fall back to state-level URL (if exists)
	// For now, North Carolina has a state-level URL
	if (normalizedState === "North Carolina") {
		return "https://portal-nc.tylertech.cloud/Portal/Home/Dashboard/29";
	}

	return null;
}

interface PropertyDetailsScreenProps {
	property: Record<string, unknown> | null; // Raw entity from backend
	isLoading?: boolean;
}

export function PropertyDetailsScreen({
	property,
	isLoading,
}: PropertyDetailsScreenProps) {
	const router = useRouter();
	const { isSubscribed, isLoading: isAuthLoading } = useOnboarding();
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isUpgrading, setIsUpgrading] = useState(false);
	const [isRequestPhotosModalOpen, setIsRequestPhotosModalOpen] =
		useState(false);
	// ... existing state ...
	const [activeSection, setActiveSection] = useState("property-details");
	const [mapStyle, setMapStyle] = useState<"streets" | "satellite">(
		"streets"
	);
	const [expandedSections, setExpandedSections] = useState({
		property: true,
		parcel: true,
		owner: true,
		financial: true,
		location: true,
		photos: true,
		locationMap: true,
		legalAuction: false,
		technical: false,
		census: false,
		landRecord: false,
		valueRecord: false,
		taxRecord: false,
		administrativeRecord: false,
		additionalDetailsRecord: false,
		recentSales: false,
		deedHistory: false,
		propertyCharacteristics: false,
		countyResources: false,
	});

	const [copiedParcelField, setCopiedParcelField] = useState<string | null>(
		null
	);
	const copyParcelValue = (text: string, fieldKey: string) => {
		navigator.clipboard.writeText(text);
		setCopiedParcelField(fieldKey);
		toast.success("Copied to clipboard");
		setTimeout(() => setCopiedParcelField(null), 2000);
	};

	const [copiedInfoCardKey, setCopiedInfoCardKey] = useState<string | null>(
		null
	);
	const copyInfoCardValue = (text: string, cardKey: string) => {
		navigator.clipboard.writeText(text);
		setCopiedInfoCardKey(cardKey);
		toast.success("Copied to clipboard");
		setTimeout(() => setCopiedInfoCardKey(null), 2000);
	};

	const mapContainer = useRef<HTMLDivElement>(null);
	const map = useRef<mapboxgl.Map | null>(null);

	const toggleSection = (section: keyof typeof expandedSections) => {
		setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
	};

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	// Check auth status on mount
	useEffect(() => {
		setIsLoggedIn(isAuthenticatedSync());
	}, []);

	// Single CTA for all locked sections: signup link (unsigned) or checkout button (signed in)
	const lockedTrialCtaNode = isLoggedIn ? (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation();
				handleUpgrade();
			}}
			className="underline hover:text-[#cbf7a6] focus:outline-none focus:ring-2 focus:ring-[#A7FF5E] focus:ring-offset-2 focus:ring-offset-transparent rounded bg-transparent border-0 cursor-pointer p-0 text-inherit text-xs font-semibold"
		>
			14-Day Free Trial Available
		</button>
	) : (
		<Link
			href="/signup"
			className="underline hover:text-[#cbf7a6] focus:outline-none focus:ring-2 focus:ring-[#A7FF5E] focus:ring-offset-2 focus:ring-offset-transparent rounded cursor-pointer"
			onClick={(e) => e.stopPropagation()}
		>
			14-Day Free Trial Available
		</Link>
	);

	// Intersection Observer for SideNav
	useEffect(() => {
		if (!isSubscribed || !property) return;

		const observerOptions = {
			root: null,
			rootMargin: "-20% 0px -70% 0px",
			threshold: 0,
		};

		const observerCallback = (entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					setActiveSection(entry.target.id);
				}
			});
		};

		const observer = new IntersectionObserver(
			observerCallback,
			observerOptions
		);

		// Wait for DOM to be ready, then observe all sections
		const timeoutId = setTimeout(() => {
			const sections = [
				"property-details",
				"parcel-details",
				"photos",
				"legal-auction",
				"technical-specs",
				"census-demographics",
				"owner-info",
				"land-record",
				"value-record",
				"tax-record",
				"administrative-record",
				"additional-details-record",
				"location-map",
			];

			sections.forEach((id) => {
				const element = document.getElementById(id);
				if (element) observer.observe(element);
			});
		}, 100);

		return () => {
			clearTimeout(timeoutId);
			observer.disconnect();
		};
	}, [isSubscribed, property]);

	// Map Initialization logic (strictly functional, styling from Figma)
	useEffect(() => {
		if (!mapContainer.current || !property) return;

		// Remove existing map if any (to handle style changes)
		if (map.current) {
			map.current.remove();
			map.current = null;
		}

		// Helper function to safely get nested property
		const getNestedValue = (
			obj: Record<string, unknown> | undefined,
			key: string
		): unknown => {
			if (!obj || typeof obj !== "object") return undefined;
			return obj[key];
		};

		// Helper function to safely get number from unknown
		const getNumber = (value: unknown): number | undefined => {
			if (typeof value === "number") return value;
			if (typeof value === "string") {
				const parsed = parseFloat(value);
				return isNaN(parsed) ? undefined : parsed;
			}
			return undefined;
		};

		const geoLocation = getNestedValue(property, "geo_location") as
			| Record<string, unknown>
			| undefined;
		const parcelData = getNestedValue(property, "parcel_data") as
			| Record<string, unknown>
			| undefined;
		const parcelDataAlt = getNestedValue(property, "parcelData") as
			| Record<string, unknown>
			| undefined;

		const lat =
			getNumber(property.latitude) ??
			getNumber(geoLocation?.lat) ??
			getNumber(parcelData?.lat) ??
			getNumber(parcelDataAlt?.lat) ??
			39.8283;
		const lng =
			getNumber(property.longitude) ??
			getNumber(geoLocation?.lon) ??
			getNumber(geoLocation?.lng) ??
			getNumber(parcelData?.lon) ??
			getNumber(parcelData?.lng) ??
			getNumber(parcelDataAlt?.lon) ??
			getNumber(parcelDataAlt?.lng) ??
			-98.5795;

		map.current = new mapboxgl.Map({
			container: mapContainer.current,
			style:
				mapStyle === "streets"
					? "mapbox://styles/mapbox/streets-v12"
					: "mapbox://styles/mapbox/satellite-streets-v12",
			center: [lng, lat],
			zoom: 17,
			pitch: 45,
			projection: { name: "globe" as const },
		});

		// Add atmosphere for "Earth" look
		map.current.on("style.load", () => {
			if (map.current) {
				map.current.setFog({
					color: "rgb(186, 210, 235)", // Lower atmosphere
					"high-color": "rgb(36, 92, 223)", // Upper atmosphere
					"horizon-blend": 0.02, // Atmosphere thickness
					"space-color": "rgb(11, 11, 25)", // Background color
					"star-intensity": 0.6, // Background star brightness
				});
			}
		});

		const marker = new mapboxgl.Marker({ color: "#A7FF5E" })
			.setLngLat([lng, lat])
			.addTo(map.current);

		return () => {
			marker.remove();
			map.current?.remove();
			map.current = null;
		};
	}, [property, mapStyle]);

	const getCoordinates = () => {
		if (!property) return { lat: undefined, lng: undefined };

		// Helper function to safely get nested property
		const getNestedValue = (
			obj: Record<string, unknown> | undefined | null,
			key: string
		): unknown => {
			if (!obj || typeof obj !== "object") return undefined;
			return obj[key];
		};

		// Helper function to safely get number from unknown
		const getNumber = (value: unknown): number | undefined => {
			if (typeof value === "number") return value;
			if (typeof value === "string") {
				const parsed = parseFloat(value);
				return isNaN(parsed) ? undefined : parsed;
			}
			return undefined;
		};

		const geoLocation = getNestedValue(property, "geo_location") as
			| Record<string, unknown>
			| undefined;
		const parcelData = getNestedValue(property, "parcel_data") as
			| Record<string, unknown>
			| undefined;
		const parcelDataAlt = getNestedValue(property, "parcelData") as
			| Record<string, unknown>
			| undefined;

		const lat =
			getNumber(property?.latitude) ??
			getNumber(geoLocation?.lat) ??
			getNumber(parcelData?.lat) ??
			getNumber(parcelDataAlt?.lat);
		const lng =
			getNumber(property?.longitude) ??
			getNumber(geoLocation?.lon) ??
			getNumber(geoLocation?.lng) ??
			getNumber(parcelData?.lon) ??
			getNumber(parcelData?.lng) ??
			getNumber(parcelDataAlt?.lon) ??
			getNumber(parcelDataAlt?.lng);
		return { lat, lng };
	};

	const formatCurrency = (value: unknown) => {
		const num =
			typeof value === "number"
				? value
				: typeof value === "string"
					? parseFloat(String(value).replace(/,/g, ""))
					: NaN;
		if (typeof num !== "number" || !Number.isFinite(num)) return "—";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(num);
	};

	// Sale amount only: hide invalid/non-numeric values (no $NaN, no N/A for currency)
	const formatSaleAmount = (value: unknown): string => {
		if (value == null || value === "") return "—";
		const str = String(value).trim();
		if (!str) return "—";
		const num = parseFloat(str.replace(/,/g, ""));
		if (!Number.isFinite(num)) return "—";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(num);
	};

	// ... (skipping to the loader block)

	if (isLoading || isAuthLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#16251c]" />
			</div>
		);
	}

	if (!property) return null;

	// Helper function to safely get nested object from Record<string, unknown>
	const getNestedObject = (
		obj: Record<string, unknown>,
		key: string
	): Record<string, unknown> => {
		const value = obj[key];
		if (value && typeof value === "object" && !Array.isArray(value)) {
			return value as Record<string, unknown>;
		}
		return {};
	};

	// Helper function to safely get value from Record<string, unknown>
	const getValue = (
		obj: Record<string, unknown> | undefined,
		key: string
	): unknown => {
		if (!obj) return undefined;
		return obj[key];
	};

	// Humanize key function - converts snake_case/camelCase to readable labels
	const humanizeKey = (key: string): string => {
		// Check if there's a display name mapping
		const displayNames = getNestedObject(property, "_displayNames");
		const displayName = getValue(displayNames, key) as string | undefined;
		if (displayName) return displayName;

		// Convert snake_case and camelCase to readable format
		return key
			.replace(/([A-Z])/g, " $1") // Add space before capital letters
			.replace(/_/g, " ") // Replace underscores with spaces
			.replace(/\b\w/g, (l) => l.toUpperCase()) // Capitalize first letter of each word
			.trim();
	};

	// Format value helper
	const formatValue = (value: unknown, key?: string): string => {
		if (value === null || value === undefined) return "N/A";
		if (typeof value === "boolean") return value ? "Yes" : "No";
		if (typeof value === "number") {
			// Check if it's a currency-related field or large number
			const currencyKeys = [
				"value",
				"price",
				"amount",
				"cost",
				"tax",
				"parval",
				"landval",
				"improvval",
				"agval",
			];
			const isCurrencyField =
				key &&
				currencyKeys.some((ck) => key.toLowerCase().includes(ck));
			if (isCurrencyField || (value > 100 && value % 1 === 0)) {
				return formatCurrency(value);
			}
			return value.toString();
		}
		if (typeof value === "string") {
			if (value.trim() === "" || value === "—" || value === "-")
				return "N/A";
			return value;
		}
		if (Array.isArray(value)) {
			return value.length > 0 ? value.join(", ") : "N/A";
		}
		if (typeof value === "object") {
			return JSON.stringify(value);
		}
		return String(value);
	};

	// Skip relation record identifiers; do not display or use for "has valid data"
	const isRelationIdKey = (key: string): boolean => {
		const k = key.toLowerCase();
		return k === "id" || k === "real_estate_id" || k === "real_id";
	};

	// Helper function to check if a section has valid (non-N/A) data, ignoring id and real_estate_id
	const hasValidData = (data: Record<string, unknown>): boolean => {
		return Object.entries(data).some(([key, value]) => {
			if (isRelationIdKey(key)) return false;
			const formattedValue = formatValue(value, key);
			return formattedValue !== "N/A";
		});
	};

	// Extract relations object
	const relations = getNestedObject(property, "relations");

	// Robust field extraction with parcel_data fallbacks and relations
	const parcel =
		getNestedObject(property, "parcelData") ||
		getNestedObject(property, "parcel_data") ||
		{};

	// Extract scraped parcel data
	const scrapedParcelData = (getNestedObject(property, "scrapedParcelData") ||
		getNestedObject(property, "scraped_parcel_data")) as unknown as
		| ScrapedParcelData
		| undefined;

	// All $0 means no data: parse amounts and treat all-zero as empty
	const parseAmount = (value: string | null | undefined): number => {
		if (value == null || value === "") return 0;
		const num = parseFloat(String(value).replace(/[,$]/g, ""));
		return Number.isFinite(num) ? num : 0;
	};
	const hasRecentSalesData =
		(scrapedParcelData?.recent_sales?.length ?? 0) > 0 &&
		scrapedParcelData!.recent_sales!.some(
			(s) => parseAmount(s.sale_amount) > 0
		);
	const deedHistory = scrapedParcelData?.details_page?.json?.deed_history;
	const hasDeedData =
		(deedHistory?.length ?? 0) > 0 &&
		deedHistory!.some((d) => parseAmount(d.sale_amount) > 0);

	// Debug: Log parcel data to console
	if (process.env.NODE_ENV === "development") {
		console.log("Parcel data extracted:", parcel);
		console.log("Parcel keys count:", Object.keys(parcel).length);
		console.log("Scraped parcel data:", scrapedParcelData);
	}
	const land =
		getNestedObject(relations, "landRecord") ||
		getNestedObject(property, "land_record") ||
		{};
	const value =
		getNestedObject(relations, "valueRecord") ||
		getNestedObject(property, "value_record") ||
		{};
	const tax =
		getNestedObject(relations, "taxRecord") ||
		getNestedObject(property, "tax_record") ||
		{};
	const owner =
		getNestedObject(relations, "ownerRecord") ||
		getNestedObject(property, "owner_record") ||
		{};
	const legal =
		getNestedObject(relations, "legalRecord") ||
		getNestedObject(property, "legal_record") ||
		{};
	const administrative =
		getNestedObject(relations, "administrativeRecord") ||
		getNestedObject(property, "administrative_record") ||
		{};
	const additionalDetails =
		getNestedObject(relations, "additionalDetailsRecord") ||
		getNestedObject(property, "additional_details_record") ||
		{};
	const auctionRecord = getNestedObject(property, "auction_record");

	// Root-level property ID only (do not use relation records' id or real_estate_id)
	const rootPropertyId =
		(getValue(property, "propertyId") as string | undefined) ||
		(getValue(property, "property_id") as string | undefined);

	// Parcel display entries (exclude id, real_estate_id, and other internal keys)
	const parcelDisplayEntries = Object.entries(parcel).filter(([key]) => {
		if (isRelationIdKey(key)) return false;
		const k = key.toLowerCase();
		return k !== "version" && k !== "propertyid" && !k.startsWith("_");
	});

	// Parcel data categories for grouped display (separator lines + labels, no dropdowns)
	const PARCEL_CATEGORY_ORDER = [
		"Location & coordinates",
		"Original Address",
		"Legal / recording",
		"Physical / lot & area",
		"Use & zoning",
		"Other",
	] as const;
	const getParcelCategory = (
		key: string
	): (typeof PARCEL_CATEGORY_ORDER)[number] => {
		const k = key.toLowerCase();
		if (k === "original_address") return "Original Address";
		if (
			[
				"lat",
				"lon",
				"latitude",
				"longitude",
				"city",
				"path",
				"address",
				"state",
				"zip",
				"county",
				"scity",
				"state2",
				"szip5",
			].includes(k)
		)
			return "Location & coordinates";
		if (
			[
				"book",
				"page",
				"plat",
				"parcel_id",
				"apn",
				"parcel_number",
				"block",
				"parcelid",
			].includes(k)
		)
			return "Legal / recording";
		if (
			[
				"lot",
				"sqft",
				"lot_size",
				"land_area",
				"gisacre",
				"ll_gissqft",
				"numstories",
				"numunits",
			].includes(k)
		)
			return "Physical / lot & area";
		if (
			[
				"qoz",
				"zoning",
				"usecode",
				"usedesc",
				"structstyle",
				"zoning_description",
				"urbanization",
				"census_tract",
			].includes(k)
		)
			return "Use & zoning";
		return "Other";
	};
	const parcelByCategory = (() => {
		const map = new Map<string, [string, unknown][]>();
		for (const entry of parcelDisplayEntries) {
			const cat = getParcelCategory(entry[0]);
			if (!map.has(cat)) map.set(cat, []);
			map.get(cat)!.push(entry);
		}
		return PARCEL_CATEGORY_ORDER.filter((cat) => map.get(cat)?.length).map(
			(cat) => ({ category: cat, entries: map.get(cat)! })
		);
	})();

	// Parse original_address value (may be JSON string) into key-value rows for "Original Address" category
	const getOriginalAddressRows = (value: unknown): [string, unknown][] => {
		if (value == null) return [];
		let obj: Record<string, unknown>;
		if (typeof value === "string") {
			try {
				obj = JSON.parse(value) as Record<string, unknown>;
			} catch {
				return [["Address", value]];
			}
		} else if (
			typeof value === "object" &&
			value !== null &&
			!Array.isArray(value)
		) {
			obj = value as Record<string, unknown>;
		} else {
			return [["Address", String(value)]];
		}
		return Object.entries(obj).filter(([, v]) => v != null && v !== "");
	};

	const yearBuilt =
		(getValue(land, "year_built") as string | number | undefined) ||
		(getValue(property, "yearBuilt") as string | number | undefined) ||
		(getValue(parcel, "yearbuilt") as string | number | undefined) ||
		"N/A";
	const propType =
		(getValue(land, "property_type") as string | undefined) ||
		(getValue(parcel, "usedesc") as string | undefined) ||
		(getValue(parcel, "usecode") as string | undefined) ||
		"Residential";

	// Lot Size calculation
	let lotSize =
		(getValue(land, "land_area") as string | undefined) ||
		(getValue(land, "lot_size") as string | undefined) ||
		"N/A";
	if (lotSize === "N/A") {
		const gisacre = getValue(parcel, "gisacre");
		const sqft = getValue(parcel, "sqft");
		const llGissqft = getValue(parcel, "ll_gissqft");
		if (gisacre) lotSize = `${Number(gisacre).toFixed(2)} acres`;
		else if (sqft) lotSize = `${Number(sqft).toLocaleString()} sq ft`;
		else if (llGissqft)
			lotSize = `${Number(llGissqft).toLocaleString()} sq ft`;
	}

	const assessedValue =
		(getValue(value, "assessed_value") as number | undefined) ||
		(getValue(parcel, "parval") as number | undefined) ||
		"N/A";
	const taxStatus =
		(getValue(tax, "tax_status") as string | undefined) ||
		(getValue(parcel, "taxyear")
			? `Current (${getValue(parcel, "taxyear")})`
			: "Current");
	const ownerName =
		(getValue(owner, "owner") as string | undefined) ||
		(getValue(owner, "owner_name") as string | undefined) ||
		(getValue(parcel, "owner") as string | undefined) ||
		(getValue(property, "owner") as string | undefined) ||
		"N/A";
	const parcelId =
		(getValue(land, "parcel_no") as string | undefined) ||
		(getValue(getNestedObject(property, "parcelData"), "parcel_id") as
			| string
			| undefined) ||
		(getValue(parcel, "parcelnumb") as string | undefined) ||
		(getValue(parcel, "alt_parcelnumb1") as string | undefined) ||
		"N/A";
	const landValue =
		(getValue(land, "land_value") as number | undefined) ||
		(getValue(value, "land_value") as number | undefined) ||
		(getValue(parcel, "landval") as number | undefined) ||
		"N/A";
	const subdivision =
		(getValue(legal, "subdivision") as string | undefined) ||
		(getValue(parcel, "subdivision") as string | undefined) ||
		"N/A";

	const mailingAddress =
		(getValue(owner, "mailing_address") as string | undefined) ||
		(getValue(owner, "owner_address") as string | undefined) ||
		(getValue(parcel, "mailadd") as string | undefined) ||
		(getValue(property, "mailingAddress") as string | undefined) ||
		"N/A";
	const physicalAddress =
		(getValue(property, "address") as string | undefined) ||
		(getValue(parcel, "address") as string | undefined) ||
		"N/A";

	// New Data Extraction for Expanded Sections
	const upsetAmount =
		(getValue(legal, "upset_amount") as number | string | undefined) ||
		"N/A";
	const judgmentAmount =
		(getValue(legal, "approx_judgement") as number | string | undefined) ||
		(getValue(legal, "judgement_amount") as number | string | undefined) ||
		"N/A";
	const plantiff =
		(getValue(legal, "plantiff") as string | undefined) || "N/A";
	const sheriffId =
		(getValue(legal, "sherrif") as string | undefined) || "N/A";
	const courtCase =
		(getValue(legal, "court_case") as string | undefined) ||
		(getValue(land, "case_number") as string | undefined) ||
		(getValue(parcel, "parcelnumb") as string | undefined) ||
		"N/A";
	const saleDate =
		(getValue(legal, "sale_date") as string | undefined) ||
		(getValue(property, "saleDate") as string | undefined) ||
		(getValue(auctionRecord, "auction_date") as string | undefined) ||
		"N/A";

	const zoning = (getValue(parcel, "zoning") as string | undefined) || "N/A";
	const zoningDesc =
		(getValue(parcel, "zoning_description") as string | undefined) || "N/A";
	const structStyle =
		(getValue(parcel, "structstyle") as string | undefined) || "N/A";
	const stories =
		(getValue(parcel, "numstories") as number | string | undefined) ||
		"N/A";
	const units =
		(getValue(parcel, "numunits") as number | string | undefined) || "N/A";
	const rooms =
		(getValue(parcel, "numrooms") as number | string | undefined) || "N/A";

	const censusTract =
		(getValue(parcel, "census_tract") as string | undefined) || "N/A";
	const censusBlock =
		(getValue(parcel, "census_block") as string | undefined) || "N/A";
	const qozStatus = (getValue(parcel, "qoz") as string | undefined) || "N/A";
	const urbanization =
		(getValue(parcel, "urbanization") as string | undefined) || "N/A";

	const hasTechnicalData =
		zoning !== "N/A" ||
		zoningDesc !== "N/A" ||
		structStyle !== "N/A" ||
		stories !== "N/A" ||
		units !== "N/A" ||
		rooms !== "N/A";
	const hasCensusData =
		censusTract !== "N/A" ||
		censusBlock !== "N/A" ||
		qozStatus !== "N/A" ||
		urbanization !== "N/A";

	const { lat, lng } = getCoordinates();
	const hasMapData =
		lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);

	return (
		<>
			<div className="min-h-screen bg-gradient-to-br from-[#f8fdf4] to-white">
				{/* Header */}
				<header className="bg-[#16251c] border-b border-black/20 sticky top-0 z-40 backdrop-blur-lg">
					<div className="max-w-7xl mx-auto px-8 py-5">
						<div className="flex items-center justify-between">
							{/* Logo */}
							<Link
								href="/"
								className="flex items-center gap-3 bg-white/10 rounded-full px-3 py-2 pr-6 backdrop-blur-sm hover:bg-white/20 transition-all cursor-pointer"
							>
								<div className="w-10 h-10 rounded-full bg-[#A7FF5E] flex items-center justify-center shadow-lg">
									<Building className="w-6 h-6 text-[#1B251F]" />
								</div>
								<span className="text-[#edffde] font-bold text-base tracking-wide">
									Tax Lien Vest
								</span>
							</Link>

							{/* Center CTA */}
							<div className="flex-1 flex justify-center">
								<button
									data-testid="find-similar-properties-button"
									onClick={() => {
										if (!property) return;

										// Get state and county from property
										const state = String(
											getValue(property, "state") || ""
										);
										const county = String(
											getValue(property, "county") || ""
										);

										if (!state || !county) {
											toast.error(
												"State and county information not available"
											);
											return;
										}

										if (isLoggedIn) {
											// Redirect to dashboard with county/state filter
											router.push(
												`/dashboard?county=${encodeURIComponent(county)}&state=${encodeURIComponent(state)}`
											);
										} else {
											// Redirect to playground with countyState filter
											const countyState = `${county}, ${state}`;
											router.push(
												`/playground?countyState=${encodeURIComponent(countyState)}`
											);
										}
									}}
									className="flex items-center gap-2 px-6 py-2.5 bg-white/5 border border-white/10 rounded-full text-white/80 hover:bg-white/10 hover:text-white transition-all text-sm font-medium"
								>
									<Search className="w-4 h-4" />
									Find similar properties
								</button>
							</div>

							{/* Actions */}
							<div className="flex items-center gap-4">
								{isLoggedIn ? (
									<Link
										data-testid="profile-button"
										href="/profile"
										className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-2 pr-5 backdrop-blur-sm hover:bg-white/20 transition-all cursor-pointer"
									>
										<div className="w-7 h-7 rounded-full bg-[#edffde] flex items-center justify-center">
											<User className="w-4 h-4 text-[#2769AF]" />
										</div>
										<span className="text-white font-semibold text-sm">
											Profile
										</span>
									</Link>
								) : (
									<Link
										href="/login"
										className="flex items-center gap-2 bg-[#A7FF5E] hover:bg-[#cbf7a6] text-[#16251c] px-6 py-2 rounded-full font-bold text-sm transition-all shadow-lg"
									>
										<LogIn className="w-4 h-4" />
										Login
									</Link>
								)}
							</div>
						</div>
					</div>
				</header>

				{/* Hero Section */}
				<div className="bg-gradient-to-br from-[#16251c] to-[#0f1b13] text-white">
					<div className="max-w-7xl mx-auto px-8 py-8">
						{/* Breadcrumb - Match Image Reference */}
						<div
							data-testid="breadcrumb-container"
							className="flex items-center gap-2 text-sm text-white/50 mb-8 font-medium"
						>
							<Link
								data-testid="breadcrumb-properties-link"
								href="/dashboard"
								className="hover:text-white cursor-pointer transition-colors"
							>
								Properties
							</Link>
							<span
								data-testid="breadcrumb-separator"
								className="text-white/20"
							>
								/
							</span>
							<Link
								data-testid="breadcrumb-county-link"
								href={`/dashboard?county=${encodeURIComponent(String(getValue(property, "county") || ""))}&state=${encodeURIComponent(String(getValue(property, "state") || ""))}`}
								className="hover:text-white cursor-pointer transition-colors capitalize"
							>
								{String(getValue(property, "county") || "")}
							</Link>
							<span
								data-testid="breadcrumb-separator"
								className="text-white/20"
							>
								/
							</span>
							<span
								data-testid="breadcrumb-apm-text"
								className="text-[#A7FF5E]"
							>
								{parcelId !== "N/A"
									? `APN-${parcelId}`
									: String(
											getValue(property, "propertyId") ||
												""
										).replace("LATLNG_CUSTOM_", "")}
							</span>
						</div>
						{/* Property Title */}
						<div className="flex items-start justify-between mb-8">
							<div>
								<h1
									data-testid="property-title"
									className="text-5xl font-extrabold tracking-tight mb-3"
								>
									{parcelId !== "N/A"
										? `APN-${parcelId}`
										: String(
												getValue(
													property,
													"propertyId"
												) || ""
											).replace("LATLNG_CUSTOM_", "")}
								</h1>
								<div className="flex items-center gap-6 text-white/50">
									<div className="flex items-center gap-2.5">
										<MapPin className="w-5 h-5 text-[#A7FF5E]" />
										<span
											data-testid="property-address"
											className="text-xl font-medium"
										>
											{physicalAddress}
										</span>
									</div>
									<div className="w-1.5 h-1.5 rounded-full bg-white/20" />
									<div className="flex items-center gap-2.5">
										<Calendar className="w-5 h-5 text-[#A7FF5E]" />
										<span
											data-testid="property-built-year"
											className="text-xl font-medium"
										>
											Built {yearBuilt}
										</span>
									</div>
								</div>
							</div>

							{!isSubscribed && (
								<button
									data-testid="upgrade-to-pro-button"
									onClick={handleUpgrade}
									disabled={isUpgrading}
									className="px-6 py-3 bg-gradient-to-r from-[#A7FF5E] to-[#cbf7a6] text-[#16251c] rounded-full font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
								>
									{isUpgrading ? (
										<Loader2 className="w-5 h-5 animate-spin" />
									) : (
										<Sparkles className="w-5 h-5" />
									)}
									{isUpgrading
										? "Redirecting..."
										: "Upgrade to Pro"}
								</button>
							)}
						</div>
						{/* Quick Stats */}
						<div className="grid grid-cols-4 gap-4">
							<StatCard
								icon={<Home className="w-5 h-5" />}
								label="Property Type"
								value={propType}
							/>
							<StatCard
								icon={<Ruler className="w-5 h-5" />}
								label="Lot Size"
								value={lotSize}
							/>
							<StatCard
								icon={<DollarSign className="w-5 h-5" />}
								label="Assessed Value"
								value={formatCurrency(assessedValue)}
							/>
							<StatCard
								icon={<TrendingUp className="w-5 h-5" />}
								label="Tax Status"
								value={taxStatus}
							/>
						</div>
					</div>
				</div>

				{/* Main Content */}
				<div className="max-w-7xl mx-auto px-8 py-10">
					<div className="flex gap-10">
						{/* Left Column - Side Nav (Subscribed Only) */}
						{isSubscribed && (
							<div className="w-64 flex-shrink-0">
								<div
									data-testid="left-navigation-menu"
									className="sticky top-28 space-y-1 border-l border-gray-100 ml-2"
								>
									<NavButton
										id="property-details"
										label="Property Details"
										active={
											activeSection === "property-details"
										}
									/>
									<NavButton
										id="parcel-details"
										label="Parcel Details"
										active={
											activeSection === "parcel-details"
										}
									/>
									<NavButton
										id="photos"
										label="Photos"
										active={activeSection === "photos"}
									/>
									{hasValidData(legal) && (
										<NavButton
											id="legal-auction"
											label="Legal & Auction"
											active={
												activeSection ===
												"legal-auction"
											}
										/>
									)}
									{hasTechnicalData && (
										<NavButton
											id="technical-specs"
											label="Technical Specs"
											active={
												activeSection ===
												"technical-specs"
											}
										/>
									)}
									{hasCensusData && (
										<NavButton
											id="census-demographics"
											label="Census Data"
											active={
												activeSection ===
												"census-demographics"
											}
										/>
									)}
									{hasValidData(owner) && (
										<NavButton
											id="owner-info"
											label="Owner Information"
											active={
												activeSection === "owner-info"
											}
										/>
									)}
									{hasValidData(land) && (
										<NavButton
											id="land-record"
											label="Land Record"
											active={
												activeSection === "land-record"
											}
										/>
									)}
									{hasValidData(value) && (
										<NavButton
											id="value-record"
											label="Value Record"
											active={
												activeSection === "value-record"
											}
										/>
									)}
									{hasValidData(tax) && (
										<NavButton
											id="tax-record"
											label="Tax Record"
											active={
												activeSection === "tax-record"
											}
										/>
									)}
									{hasValidData(administrative) && (
										<NavButton
											id="administrative-record"
											label="Administrative"
											active={
												activeSection ===
												"administrative-record"
											}
										/>
									)}
									{hasValidData(additionalDetails) && (
										<NavButton
											id="additional-details-record"
											label="Additional Details"
											active={
												activeSection ===
												"additional-details-record"
											}
										/>
									)}
									{hasMapData && (
										<NavButton
											id="location-map"
											label="Property Map"
											active={
												activeSection === "location-map"
											}
										/>
									)}
								</div>
							</div>
						)}

						<div className="flex-1 space-y-8 max-w-[800px]">
							{/* Property Basic Information */}
							<CollapsibleSection
								id="property-details"
								title="Property Details"
								icon={<Home className="w-5 h-5" />}
								isExpanded={expandedSections.property}
								onToggle={() => toggleSection("property")}
								locked={false}
							>
								<div className="grid grid-cols-2 gap-6">
									<InfoCard
										label="Property Owner"
										value={ownerName}
										icon={<User className="w-4 h-4" />}
										onCopy={copyInfoCardValue}
										copiedCardKey={copiedInfoCardKey}
									/>
									<InfoCard
										label="Co-Owner Name"
										value={String(
											getValue(parcel, "owner2") || "N/A"
										)}
										icon={<User className="w-4 h-4" />}
										onCopy={copyInfoCardValue}
										copiedCardKey={copiedInfoCardKey}
									/>
									<InfoCard
										label="Owner Email"
										value={String(
											getValue(owner, "owner_email") ||
												"N/A"
										)}
										icon={<Mail className="w-4 h-4" />}
										onCopy={copyInfoCardValue}
										copiedCardKey={copiedInfoCardKey}
									/>
									{courtCase !== "N/A" && (
										<InfoCard
											label="Court Case"
											value={String(courtCase)}
											icon={
												<Shield className="w-4 h-4" />
											}
											courtCaseUrl={getCourtCaseUrl(
												String(
													getValue(
														property,
														"county"
													) || ""
												),
												String(
													getValue(
														property,
														"state"
													) || ""
												)
											)}
											courtCaseNumber={String(courtCase)}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
									)}
								</div>
								<div className="mt-6 grid grid-cols-1 gap-6">
									<InfoCard
										label="Mailing Address"
										value={mailingAddress}
										icon={<MapPin className="w-4 h-4" />}
										fullWidth
										onCopy={copyInfoCardValue}
										copiedCardKey={copiedInfoCardKey}
									/>
									<InfoCard
										label="Physical Address"
										value={physicalAddress}
										icon={<Home className="w-4 h-4" />}
										fullWidth
										onCopy={copyInfoCardValue}
										copiedCardKey={copiedInfoCardKey}
									/>
								</div>

								{/* Property Characteristics from Scraped Data — only show fields with real values (skip N/A) */}
								{scrapedParcelData?.property_characteristics
									?.json &&
									(() => {
										const pc =
											scrapedParcelData
												.property_characteristics.json;
										const na = "N/A";
										const str = (v: unknown) =>
											v != null && v !== ""
												? String(v).trim()
												: na;
										const resSummary =
											pc.residential_summary
												? [
														{
															label: "Style",
															value: str(
																pc
																	.residential_summary
																	.style
															),
															icon: (
																<Home className="w-4 h-4" />
															),
														},
														{
															label: "Total Area",
															value: str(
																pc
																	.residential_summary
																	.total_area
															),
															icon: (
																<Ruler className="w-4 h-4" />
															),
														},
														{
															label: "Year Built",
															value: str(
																pc
																	.residential_summary
																	.year_built
															),
															icon: (
																<Calendar className="w-4 h-4" />
															),
														},
														{
															label: "Card Number",
															value: str(
																pc
																	.residential_summary
																	.card_number
															),
															icon: (
																<FileText className="w-4 h-4" />
															),
														},
													].filter(
														(f) => f.value !== na
													)
												: [];
										const building =
											pc.residential_ag_building
												? [
														{
															label: "Condition",
															value: str(
																pc
																	.residential_ag_building
																	.condition
															),
															icon: (
																<Shield className="w-4 h-4" />
															),
														},
														{
															label: "Stories",
															value: str(
																pc
																	.residential_ag_building
																	.number_of_stories
															),
															icon: (
																<Building className="w-4 h-4" />
															),
														},
														{
															label: "Bedrooms",
															value: str(
																pc
																	.residential_ag_building
																	.number_of_bedrooms
															),
															icon: (
																<Home className="w-4 h-4" />
															),
														},
														{
															label: "Full Baths",
															value: str(
																pc
																	.residential_ag_building
																	.number_of_full_baths
															),
															icon: (
																<Home className="w-4 h-4" />
															),
														},
														{
															label: "Half Baths",
															value: str(
																pc
																	.residential_ag_building
																	.number_of_half_baths
															),
															icon: (
																<Home className="w-4 h-4" />
															),
														},
														{
															label: "Total Fixtures",
															value: str(
																pc
																	.residential_ag_building
																	.total_fixtures
															),
															icon: (
																<Home className="w-4 h-4" />
															),
														},
														{
															label: "Heat Type",
															value: str(
																pc
																	.residential_ag_building
																	.heat_type
															),
															icon: (
																<TrendingUp className="w-4 h-4" />
															),
														},
														{
															label: "Air Conditioning",
															value: str(
																pc
																	.residential_ag_building
																	.air_conditioning
															),
															icon: (
																<TrendingUp className="w-4 h-4" />
															),
														},
														{
															label: "Exterior Wall",
															value: str(
																pc
																	.residential_ag_building
																	.exterior_wall
															),
															icon: (
																<Building className="w-4 h-4" />
															),
														},
														{
															label: "Roof Type",
															value: str(
																pc
																	.residential_ag_building
																	.roof_type
															),
															icon: (
																<Building className="w-4 h-4" />
															),
														},
														{
															label: "Foundation",
															value: str(
																pc
																	.residential_ag_building
																	.foundation
															),
															icon: (
																<Building className="w-4 h-4" />
															),
														},
														{
															label: "Floor Finish",
															value: str(
																pc
																	.residential_ag_building
																	.floor_finish
															),
															icon: (
																<Home className="w-4 h-4" />
															),
														},
													].filter(
														(f) => f.value !== na
													)
												: [];
										const hasAny =
											resSummary.length > 0 ||
											building.length > 0;
										if (!hasAny) return null;
										return (
											<div className="mt-8 pt-6 border-t border-gray-200">
												<h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
													<Building className="w-5 h-5" />
													Property Characteristics
												</h3>
												{resSummary.length > 0 && (
													<div className="mb-6">
														<h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
															Residential Summary
														</h4>
														<div className="grid grid-cols-2 gap-6">
															{resSummary.map(
																(f) => (
																	<InfoCard
																		key={
																			f.label
																		}
																		label={
																			f.label
																		}
																		value={
																			f.value
																		}
																		icon={
																			f.icon
																		}
																		onCopy={
																			copyInfoCardValue
																		}
																		copiedCardKey={
																			copiedInfoCardKey
																		}
																	/>
																)
															)}
														</div>
													</div>
												)}
												{building.length > 0 && (
													<div>
														<h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
															Building Details
														</h4>
														<div className="grid grid-cols-2 gap-6">
															{building.map(
																(f) => (
																	<InfoCard
																		key={
																			f.label
																		}
																		label={
																			f.label
																		}
																		value={
																			f.value
																		}
																		icon={
																			f.icon
																		}
																		onCopy={
																			copyInfoCardValue
																		}
																		copiedCardKey={
																			copiedInfoCardKey
																		}
																	/>
																)
															)}
														</div>
													</div>
												)}
											</div>
										);
									})()}
							</CollapsibleSection>

							{/* Detailed Parcel Data - Scrollable */}
							<CollapsibleSection
								id="parcel-details"
								title="Parcel Information"
								icon={<Database className="w-5 h-5" />}
								isExpanded={expandedSections.parcel}
								onToggle={() => toggleSection("parcel")}
								locked={!isSubscribed}
								premiumBadge={!isSubscribed}
								lockedTrialCta={lockedTrialCtaNode}
								headerAction={
									isSubscribed &&
									rootPropertyId &&
									property?.state &&
									property?.county ? (
										<button
											onClick={() => {
												window.open(
													`/${property.state}/${property.county}/property/${rootPropertyId}/parcel`,
													"_blank"
												);
											}}
											className="flex items-center gap-2 text-[#417714] hover:text-[#16251c] font-medium text-sm transition-colors"
										>
											<Database className="w-4 h-4" />
											Explore Parcel Data
											<ArrowRight className="w-4 h-4" />
										</button>
									) : undefined
								}
							>
								<div className="max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
									{parcelDisplayEntries.length === 0 ? (
										<div className="text-center py-8 text-gray-500">
											<p>No parcel data available</p>
										</div>
									) : (
										<>
											{isSubscribed && (
												<div className="mt-6 pt-6 border-t border-gray-200">
													<button
														onClick={() => {
															const currentUrl =
																window.location
																	.pathname;
															window.open(
																`${currentUrl}/parcel`,
																"_blank"
															);
														}}
														className="w-full bg-[#16251c] hover:bg-[#16251c]/90 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
													>
														<Database className="w-5 h-5" />
														Explore Parcel Data
														<ArrowRight className="w-5 h-5" />
													</button>
													<p className="text-xs text-center text-gray-500 mt-2">
														Access deed history,
														recent sales, building
														details, and more
													</p>
												</div>
											)}
											<div
												className="space-y-8"
												style={{ marginTop: "2rem" }}
											>
												{parcelByCategory.map(
													(
														{ category, entries },
														idx
													) => (
														<div key={category}>
															{idx > 0 && (
																<div
																	className="border-t border-gray-200 mt-8 mb-6"
																	aria-hidden
																/>
															)}
															<h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider py-3 px-4 rounded-t-lg bg-gray-100 border border-gray-200 border-b-0">
																{category}
															</h4>
															<div className="overflow-x-auto rounded-b-lg border border-gray-200 border-t-0 -mt-px">
																<table className="w-full text-sm">
																	<thead>
																		<tr className="border-b border-gray-200 bg-gray-50">
																			<th className="text-left py-2.5 px-3 font-semibold text-gray-600">
																				Field
																			</th>
																			<th className="text-right py-2.5 px-3 font-semibold text-gray-600">
																				Value
																			</th>
																		</tr>
																	</thead>
																	<tbody className="divide-y divide-gray-100">
																		{(category ===
																		"Original Address"
																			? getOriginalAddressRows(
																					entries[0]?.[1]
																				)
																			: entries
																		).map(
																			([
																				key,
																				value,
																			]) => {
																				const formattedValue =
																					formatValue(
																						value,
																						key
																					);
																				const fieldKey = `${category}-${key}`;
																				const isCopied =
																					copiedParcelField ===
																					fieldKey;
																				return (
																					<tr
																						key={
																							key
																						}
																						className="hover:bg-gray-50/50"
																					>
																						<td className="py-2.5 px-3 text-gray-600">
																							{humanizeKey(
																								key
																							)}
																						</td>
																						<td
																							className={`py-2.5 px-3 font-medium text-right ${!isSubscribed ? "blur-sm select-none" : "text-gray-900"}`}
																						>
																							<div className="flex items-center gap-2 justify-end">
																								<span>
																									{
																										formattedValue
																									}
																								</span>
																								{isSubscribed &&
																									formattedValue !==
																										"N/A" && (
																										<button
																											type="button"
																											onClick={() =>
																												copyParcelValue(
																													formattedValue,
																													fieldKey
																												)
																											}
																											className="p-1 rounded text-gray-500 hover:text-[#417714] transition-colors shrink-0"
																											aria-label={`Copy ${humanizeKey(key)}`}
																										>
																											{isCopied ? (
																												<Check className="w-3.5 h-3.5" />
																											) : (
																												<Copy className="w-3.5 h-3.5" />
																											)}
																										</button>
																									)}
																							</div>
																						</td>
																					</tr>
																				);
																			}
																		)}
																	</tbody>
																</table>
															</div>
														</div>
													)
												)}
											</div>
										</>
									)}
								</div>
							</CollapsibleSection>

							{/* Photos Section */}
							<CollapsibleSection
								id="photos"
								title="Property Photos"
								icon={<Camera className="w-5 h-5" />}
								isExpanded={expandedSections.photos}
								onToggle={() => toggleSection("photos")}
								locked={false}
							>
								<PhotosSection
									property={property}
									setIsRequestPhotosModalOpen={
										setIsRequestPhotosModalOpen
									}
								/>
							</CollapsibleSection>

							{/* Recent Sales - Pro Only */}
							{isSubscribed && (
								<CollapsibleSection
									id="recent-sales"
									title="Recent Comparable Sales"
									icon={<TrendingUp className="w-5 h-5" />}
									isExpanded={expandedSections.recentSales}
									onToggle={() =>
										toggleSection("recentSales")
									}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										{hasRecentSalesData ? (
											<div className="overflow-x-auto">
												<table className="w-full">
													<thead>
														<tr className="border-b border-gray-200">
															<th className="text-left py-2 px-2 text-xs font-semibold text-gray-600">
																Address
															</th>
															<th className="text-left py-2 px-2 text-xs font-semibold text-gray-600">
																Parcel #
															</th>
															<th className="text-left py-2 px-2 text-xs font-semibold text-gray-600">
																Sale Date
															</th>
															<th className="text-right py-2 px-2 text-xs font-semibold text-gray-600">
																Sale Amount
															</th>
														</tr>
													</thead>
													<tbody>
														{scrapedParcelData!.recent_sales!.map(
															(sale, idx) => (
																<tr
																	key={idx}
																	className="border-b border-gray-100 hover:bg-gray-50"
																>
																	<td className="py-2 px-2 text-sm text-gray-900">
																		{sale.address ||
																			"N/A"}
																	</td>
																	<td className="py-2 px-2 text-sm text-gray-600 font-mono">
																		{sale.parcel_number ||
																			"N/A"}
																	</td>
																	<td className="py-2 px-2 text-sm text-gray-600">
																		{sale.sale_date ||
																			"N/A"}
																	</td>
																	<td className="py-2 px-2 text-sm text-gray-900 font-semibold text-right">
																		{formatSaleAmount(
																			sale.sale_amount
																		)}
																	</td>
																</tr>
															)
														)}
													</tbody>
												</table>
											</div>
										) : (
											<div className="flex flex-col items-center justify-center py-12 px-4 text-center">
												<TrendingUp className="w-10 h-10 text-gray-300 mb-3" />
												<p className="text-sm font-medium text-gray-600">
													No recent sales data
													available
												</p>
												<p className="text-xs text-gray-500 mt-1">
													Recent property sales in the
													surrounding area will appear
													here when available.
												</p>
											</div>
										)}
									</div>
								</CollapsibleSection>
							)}

							{/* Deed History - Pro Only */}
							{isSubscribed && (
								<CollapsibleSection
									id="deed-history"
									title="Deed Transfer History"
									icon={<FileText className="w-5 h-5" />}
									isExpanded={expandedSections.deedHistory}
									onToggle={() =>
										toggleSection("deedHistory")
									}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										{hasDeedData ? (
											<div className="overflow-x-auto">
												<table className="w-full">
													<thead>
														<tr className="border-b border-gray-200">
															<th className="text-left py-2 px-2 text-xs font-semibold text-gray-600">
																Deed
															</th>
															<th className="text-left py-2 px-2 text-xs font-semibold text-gray-600">
																Grantee
															</th>
															<th className="text-left py-2 px-2 text-xs font-semibold text-gray-600">
																Sale Date
															</th>
															<th className="text-right py-2 px-2 text-xs font-semibold text-gray-600">
																Sale Amount
															</th>
															<th className="text-center py-2 px-2 text-xs font-semibold text-gray-600">
																Multi
															</th>
														</tr>
													</thead>
													<tbody>
														{deedHistory!.map(
															(deed, idx) => (
																<tr
																	key={idx}
																	className="border-b border-gray-100 hover:bg-gray-50"
																>
																	<td className="py-2 px-2 text-sm text-gray-900 font-mono">
																		{deed.deed ||
																			"N/A"}
																	</td>
																	<td className="py-2 px-2 text-sm text-gray-900">
																		{deed.grantee ||
																			"N/A"}
																	</td>
																	<td className="py-2 px-2 text-sm text-gray-600">
																		{deed.sale_date ||
																			"N/A"}
																	</td>
																	<td className="py-2 px-2 text-sm text-gray-900 font-semibold text-right">
																		{formatSaleAmount(
																			deed.sale_amount
																		)}
																	</td>
																	<td className="py-2 px-2 text-sm text-gray-600 text-center">
																		{deed.multi ||
																			"N/A"}
																	</td>
																</tr>
															)
														)}
													</tbody>
												</table>
											</div>
										) : (
											<div className="flex flex-col items-center justify-center py-12 px-4 text-center">
												<FileText className="w-10 h-10 text-gray-300 mb-3" />
												<p className="text-sm font-medium text-gray-600">
													No deed history available
												</p>
												<p className="text-xs text-gray-500 mt-1">
													Historical ownership
													transfers and sale amounts
													will appear here when
													available.
												</p>
											</div>
										)}
									</div>
								</CollapsibleSection>
							)}

							{/* Property Characteristics - Pro Only; only show fields with real values (skip N/A) */}
							{isSubscribed &&
								scrapedParcelData?.property_characteristics
									?.json &&
								(() => {
									const pc =
										scrapedParcelData
											.property_characteristics.json;
									const na = "N/A";
									const str = (v: unknown) =>
										v != null && v !== ""
											? String(v).trim()
											: na;
									const resSummary = pc.residential_summary
										? [
												{
													label: "Style",
													value: str(
														pc.residential_summary
															.style
													),
												},
												{
													label: "Total Area",
													value: str(
														pc.residential_summary
															.total_area
													),
												},
												{
													label: "Year Built",
													value: str(
														pc.residential_summary
															.year_built
													),
												},
												{
													label: "Card Number",
													value: str(
														pc.residential_summary
															.card_number
													),
												},
											].filter((f) => f.value !== na)
										: [];
									const building = pc.residential_ag_building
										? [
												{
													label: "Condition",
													value: str(
														pc
															.residential_ag_building
															.condition
													),
												},
												{
													label: "Stories",
													value: str(
														pc
															.residential_ag_building
															.number_of_stories
													),
												},
												{
													label: "Bedrooms",
													value: str(
														pc
															.residential_ag_building
															.number_of_bedrooms
													),
												},
												{
													label: "Full Baths",
													value: str(
														pc
															.residential_ag_building
															.number_of_full_baths
													),
												},
												{
													label: "Half Baths",
													value: str(
														pc
															.residential_ag_building
															.number_of_half_baths
													),
												},
												{
													label: "Total Fixtures",
													value: str(
														pc
															.residential_ag_building
															.total_fixtures
													),
												},
												{
													label: "Heat Type",
													value: str(
														pc
															.residential_ag_building
															.heat_type
													),
												},
												{
													label: "Air Conditioning",
													value: str(
														pc
															.residential_ag_building
															.air_conditioning
													),
												},
												{
													label: "Exterior Wall",
													value: str(
														pc
															.residential_ag_building
															.exterior_wall
													),
												},
												{
													label: "Roof Type",
													value: str(
														pc
															.residential_ag_building
															.roof_type
													),
												},
												{
													label: "Foundation",
													value: str(
														pc
															.residential_ag_building
															.foundation
													),
												},
												{
													label: "Floor Finish",
													value: str(
														pc
															.residential_ag_building
															.floor_finish
													),
												},
											].filter((f) => f.value !== na)
										: [];
									const hasAny =
										resSummary.length > 0 ||
										building.length > 0;
									if (!hasAny) return null;
									return (
										<CollapsibleSection
											id="property-characteristics"
											title="Building & Property Characteristics"
											icon={
												<Building className="w-5 h-5" />
											}
											isExpanded={
												expandedSections.propertyCharacteristics
											}
											onToggle={() =>
												toggleSection(
													"propertyCharacteristics"
												)
											}
											locked={!isSubscribed}
											premiumBadge={!isSubscribed}
											lockedTrialCta={lockedTrialCtaNode}
										>
											<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
												<div className="space-y-6">
													{resSummary.length > 0 && (
														<div>
															<h4 className="font-semibold text-gray-900 mb-3">
																Residential
																Summary
															</h4>
															<div className="grid grid-cols-2 gap-4">
																{resSummary.map(
																	(f) => (
																		<InfoCard
																			key={
																				f.label
																			}
																			label={
																				f.label
																			}
																			value={
																				f.value
																			}
																			icon={
																				<Home className="w-4 h-4" />
																			}
																			onCopy={
																				copyInfoCardValue
																			}
																			copiedCardKey={
																				copiedInfoCardKey
																			}
																		/>
																	)
																)}
															</div>
														</div>
													)}
													{building.length > 0 && (
														<div>
															<h4 className="font-semibold text-gray-900 mb-3">
																Building Details
															</h4>
															<div className="grid grid-cols-2 gap-4">
																{building.map(
																	(f) => (
																		<InfoCard
																			key={
																				f.label
																			}
																			label={
																				f.label
																			}
																			value={
																				f.value
																			}
																			icon={
																				<Building className="w-4 h-4" />
																			}
																			onCopy={
																				copyInfoCardValue
																			}
																			copiedCardKey={
																				copiedInfoCardKey
																			}
																		/>
																	)
																)}
															</div>
														</div>
													)}
												</div>
											</div>
										</CollapsibleSection>
									);
								})()}

							{/* County Resources - Pro Only */}
							{isSubscribed &&
								scrapedParcelData?.section_urls && (
									<CollapsibleSection
										id="county-resources"
										title="County Resources & Links"
										icon={
											<ExternalLinkIcon className="w-5 h-5" />
										}
										isExpanded={
											expandedSections.countyResources
										}
										onToggle={() =>
											toggleSection("countyResources")
										}
										locked={!isSubscribed}
										premiumBadge={!isSubscribed}
										lockedTrialCta={lockedTrialCtaNode}
									>
										<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
											<div className="grid md:grid-cols-2 gap-3">
												{scrapedParcelData.section_urls
													.tax_bill && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.tax_bill
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															View Tax Bill
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.view_map && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.view_map
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															View Map
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.recent_sales && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.recent_sales
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															View Recent Sales
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.tax_and_sewer_only && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.tax_and_sewer_only
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															Check Tax And Sewer
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.property_characteristics && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.property_characteristics
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															Property
															Characteristics
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.code_violation_details && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.code_violation_details
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															Code Violation
															Details
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.code_violation_fee && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.code_violation_fee
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															Code Violation Fee
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.construction_violation_details && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.construction_violation_details
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															Construction
															Violation Details
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
												{scrapedParcelData.section_urls
													.elevator_certificate && (
													<a
														href={
															scrapedParcelData
																.section_urls
																.elevator_certificate
														}
														target="_blank"
														rel="noopener noreferrer"
														className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-[#A7FF5E] hover:bg-gray-50 transition-all group"
													>
														<span className="text-sm font-medium text-gray-700 group-hover:text-[#16251c]">
															Elevator Certificate
														</span>
														<ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-[#A7FF5E]" />
													</a>
												)}
											</div>
										</div>
									</CollapsibleSection>
								)}

							{/* Legal & Auction Summary - Scrollable */}
							{hasValidData(legal) && (
								<CollapsibleSection
									id="legal-auction"
									title="Legal & Auction Summary"
									icon={<Shield className="w-5 h-5" />}
									isExpanded={expandedSections.legalAuction}
									onToggle={() =>
										toggleSection("legalAuction")
									}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										<div className="grid grid-cols-2 gap-6">
											{Object.entries(legal)
												.filter(([key, value]) => {
													if (isRelationIdKey(key))
														return false;
													const formattedValue =
														formatValue(value, key);
													return (
														formattedValue !== "N/A"
													);
												})
												.map(([key, value]) => {
													const formattedValue =
														formatValue(value, key);
													const isCourtCase =
														key === "court_case" ||
														humanizeKey(key) ===
															"Court Case";
													const isZillowWeblink =
														key ===
															"zillowWeblink" ||
														key ===
															"zillow_weblink" ||
														humanizeKey(key) ===
															"Zillow Web Link";
													const isRealtorWeblink =
														key ===
															"realtorWebLink" ||
														key ===
															"realtor_weblink" ||
														humanizeKey(key) ===
															"Realtor Web Link";
													const county = String(
														getValue(
															property,
															"county"
														) || ""
													);
													const state = String(
														getValue(
															property,
															"state"
														) || ""
													);
													const courtCaseUrl =
														isCourtCase
															? getCourtCaseUrl(
																	county,
																	state
																)
															: null;
													const courtCaseNumber =
														isCourtCase
															? formattedValue
															: undefined;
													// CRITICAL: Strict URL validation for professional real estate firms
													// URLs must be precisely formatted and valid - this is for top-tier real estate operations
													// All URLs are validated using URL constructor to ensure reliability
													const zillowUrl =
														isZillowWeblink
															? (() => {
																	if (
																		!formattedValue ||
																		formattedValue ===
																			"N/A" ||
																		formattedValue.trim() ===
																			""
																	) {
																		return null;
																	}

																	const urlValue =
																		String(
																			formattedValue
																		).trim();

																	// Validate URL format strictly
																	if (
																		urlValue.startsWith(
																			"http://"
																		) ||
																		urlValue.startsWith(
																			"https://"
																		)
																	) {
																		try {
																			// Use URL constructor to validate format
																			new URL(
																				urlValue
																			);
																			return urlValue;
																		} catch (error) {
																			// Invalid URL format
																			return null;
																		}
																	}

																	return null;
																})()
															: null;
													// CRITICAL: Strict URL validation for professional real estate firms
													// URLs must be precisely formatted and valid - this is for top-tier real estate operations
													// All URLs are validated using URL constructor to ensure reliability
													const realtorUrl =
														isRealtorWeblink
															? (() => {
																	if (
																		!formattedValue ||
																		formattedValue ===
																			"N/A" ||
																		formattedValue.trim() ===
																			""
																	) {
																		return null;
																	}

																	const urlValue =
																		String(
																			formattedValue
																		).trim();

																	// Validate URL format strictly
																	if (
																		urlValue.startsWith(
																			"http://"
																		) ||
																		urlValue.startsWith(
																			"https://"
																		)
																	) {
																		try {
																			// Use URL constructor to validate format
																			new URL(
																				urlValue
																			);
																			return urlValue;
																		} catch (error) {
																			// Invalid URL format
																			return null;
																		}
																	}

																	return null;
																})()
															: null;

													return (
														<InfoCard
															key={key}
															label={humanizeKey(
																key
															)}
															value={
																formattedValue
															}
															icon={
																<Shield className="w-4 h-4" />
															}
															blurred={
																!isSubscribed
															}
															courtCaseUrl={
																courtCaseUrl
															}
															courtCaseNumber={
																courtCaseNumber
															}
															zillowUrl={
																zillowUrl
															}
															realtorUrl={
																realtorUrl
															}
															onCopy={
																copyInfoCardValue
															}
															copiedCardKey={
																copiedInfoCardKey
															}
														/>
													);
												})}
										</div>
									</div>
								</CollapsibleSection>
							)}

							{/* Technical Specifications & Zoning */}
							{hasTechnicalData && (
								<CollapsibleSection
									id="technical-specs"
									title="Technical Specifications & Zoning"
									icon={<Ruler className="w-5 h-5" />}
									isExpanded={expandedSections.technical}
									onToggle={() => toggleSection("technical")}
									locked={false}
								>
									<div className="grid grid-cols-2 gap-6">
										<InfoCard
											label="Zoning Code"
											value={zoning}
											icon={
												<Building className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="Zoning Description"
											value={zoningDesc}
											icon={
												<FileText className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="Structure Style"
											value={structStyle}
											icon={<Home className="w-4 h-4" />}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="Number of Stories"
											value={stories.toString()}
											icon={
												<ChevronUp className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="Total Units"
											value={units.toString()}
											icon={
												<Building className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="Total Rooms"
											value={rooms.toString()}
											icon={<Home className="w-4 h-4" />}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
									</div>
								</CollapsibleSection>
							)}

							{/* Census & Demographics */}
							{hasCensusData && (
								<CollapsibleSection
									id="census-demographics"
									title="Census & Demographics"
									icon={<Database className="w-5 h-5" />}
									isExpanded={expandedSections.census}
									onToggle={() => toggleSection("census")}
									locked={false}
								>
									<div className="grid grid-cols-2 gap-6">
										<InfoCard
											label="Census Tract"
											value={censusTract}
											icon={
												<Navigation2 className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="Census Block"
											value={censusBlock}
											icon={
												<Navigation2 className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="QOZ Status"
											value={
												qozStatus === "Yes"
													? "Inside Opportunity Zone"
													: "Outside Opportunity Zone"
											}
											icon={
												<Sparkles className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
										<InfoCard
											label="Urbanization"
											value={urbanization}
											icon={
												<MapPin className="w-4 h-4" />
											}
											onCopy={copyInfoCardValue}
											copiedCardKey={copiedInfoCardKey}
										/>
									</div>
								</CollapsibleSection>
							)}

							{/* Owner Contact Information */}
							{hasValidData(owner) && (
								<CollapsibleSection
									id="owner-info"
									title="Owner Contact Information"
									icon={<Mail className="w-5 h-5" />}
									isExpanded={expandedSections.owner}
									onToggle={() => toggleSection("owner")}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										<div className="grid grid-cols-2 gap-6">
											{Object.entries(owner)
												.filter(([key, value]) => {
													if (isRelationIdKey(key))
														return false;
													const formattedValue =
														formatValue(value, key);
													return (
														formattedValue !== "N/A"
													);
												})
												.map(([key, value]) => {
													const formattedValue =
														formatValue(value, key);
													return (
														<InfoCard
															key={key}
															label={humanizeKey(
																key
															)}
															value={
																formattedValue
															}
															icon={
																<User className="w-4 h-4" />
															}
															blurred={
																!isSubscribed
															}
															onCopy={
																copyInfoCardValue
															}
															copiedCardKey={
																copiedInfoCardKey
															}
														/>
													);
												})}
										</div>
									</div>
								</CollapsibleSection>
							)}

							{/* Land Record Section - Pro Only */}
							{hasValidData(land) && (
								<CollapsibleSection
									id="land-record"
									title="Land Record"
									icon={<FileText className="w-5 h-5" />}
									isExpanded={expandedSections.landRecord}
									onToggle={() => toggleSection("landRecord")}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										<div className="grid grid-cols-2 gap-6">
											{Object.entries(land)
												.filter(([key, value]) => {
													if (isRelationIdKey(key))
														return false;
													const formattedValue =
														formatValue(value, key);
													return (
														formattedValue !== "N/A"
													);
												})
												.map(([key, value]) => {
													const formattedValue =
														formatValue(value, key);
													return (
														<InfoCard
															key={key}
															label={humanizeKey(
																key
															)}
															value={
																formattedValue
															}
															icon={
																<FileText className="w-4 h-4" />
															}
															blurred={
																!isSubscribed
															}
															onCopy={
																copyInfoCardValue
															}
															copiedCardKey={
																copiedInfoCardKey
															}
														/>
													);
												})}
										</div>
									</div>
								</CollapsibleSection>
							)}

							{/* Value Record Section - Pro Only */}
							{hasValidData(value) && (
								<CollapsibleSection
									id="value-record"
									title="Value Record"
									icon={<DollarSign className="w-5 h-5" />}
									isExpanded={expandedSections.valueRecord}
									onToggle={() =>
										toggleSection("valueRecord")
									}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										<div className="grid grid-cols-2 gap-6">
											{Object.entries(value)
												.filter(([key, value]) => {
													if (isRelationIdKey(key))
														return false;
													const formattedValue =
														formatValue(value, key);
													return (
														formattedValue !== "N/A"
													);
												})
												.map(([key, value]) => {
													const formattedValue =
														formatValue(value, key);
													return (
														<InfoCard
															key={key}
															label={humanizeKey(
																key
															)}
															value={
																formattedValue
															}
															icon={
																<DollarSign className="w-4 h-4" />
															}
															blurred={
																!isSubscribed
															}
															onCopy={
																copyInfoCardValue
															}
															copiedCardKey={
																copiedInfoCardKey
															}
														/>
													);
												})}
										</div>
									</div>
								</CollapsibleSection>
							)}

							{/* Tax Record Section - Pro Only */}
							{hasValidData(tax) && (
								<CollapsibleSection
									id="tax-record"
									title="Tax Record"
									icon={<TrendingUp className="w-5 h-5" />}
									isExpanded={expandedSections.taxRecord}
									onToggle={() => toggleSection("taxRecord")}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										<div className="grid grid-cols-2 gap-6">
											{Object.entries(tax)
												.filter(([key, value]) => {
													if (isRelationIdKey(key))
														return false;
													const formattedValue =
														formatValue(value, key);
													return (
														formattedValue !== "N/A"
													);
												})
												.map(([key, value]) => {
													const formattedValue =
														formatValue(value, key);
													return (
														<InfoCard
															key={key}
															label={humanizeKey(
																key
															)}
															value={
																formattedValue
															}
															icon={
																<TrendingUp className="w-4 h-4" />
															}
															blurred={
																!isSubscribed
															}
															onCopy={
																copyInfoCardValue
															}
															copiedCardKey={
																copiedInfoCardKey
															}
														/>
													);
												})}
										</div>
									</div>
								</CollapsibleSection>
							)}

							{/* Administrative Record Section - Pro Only */}
							{hasValidData(administrative) && (
								<CollapsibleSection
									id="administrative-record"
									title="Administrative Record"
									icon={<Shield className="w-5 h-5" />}
									isExpanded={
										expandedSections.administrativeRecord
									}
									onToggle={() =>
										toggleSection("administrativeRecord")
									}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										<div className="grid grid-cols-2 gap-6">
											{Object.entries(administrative)
												.filter(([key, value]) => {
													if (isRelationIdKey(key))
														return false;
													const formattedValue =
														formatValue(value, key);
													return (
														formattedValue !== "N/A"
													);
												})
												.map(([key, value]) => {
													const formattedValue =
														formatValue(value, key);
													return (
														<InfoCard
															key={key}
															label={humanizeKey(
																key
															)}
															value={
																formattedValue
															}
															icon={
																<Shield className="w-4 h-4" />
															}
															blurred={
																!isSubscribed
															}
															onCopy={
																copyInfoCardValue
															}
															copiedCardKey={
																copiedInfoCardKey
															}
														/>
													);
												})}
										</div>
									</div>
								</CollapsibleSection>
							)}

							{/* Additional Details Record Section - Pro Only */}
							{hasValidData(additionalDetails) && (
								<CollapsibleSection
									id="additional-details-record"
									title="Additional Details"
									icon={<Database className="w-5 h-5" />}
									isExpanded={
										expandedSections.additionalDetailsRecord
									}
									onToggle={() =>
										toggleSection("additionalDetailsRecord")
									}
									locked={!isSubscribed}
									premiumBadge={!isSubscribed}
									lockedTrialCta={lockedTrialCtaNode}
								>
									<div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
										<div className="grid grid-cols-2 gap-6">
											{Object.entries(additionalDetails)
												.filter(([key, value]) => {
													if (isRelationIdKey(key))
														return false;
													const formattedValue =
														formatValue(value, key);
													return (
														formattedValue !== "N/A"
													);
												})
												.map(([key, value]) => {
													const formattedValue =
														formatValue(value, key);
													return (
														<InfoCard
															key={key}
															label={humanizeKey(
																key
															)}
															value={
																formattedValue
															}
															icon={
																<Database className="w-4 h-4" />
															}
															blurred={
																!isSubscribed
															}
															onCopy={
																copyInfoCardValue
															}
															copiedCardKey={
																copiedInfoCardKey
															}
														/>
													);
												})}
										</div>
									</div>
								</CollapsibleSection>
							)}

							{/* Property Location Map - aligned with cards above (subscribed and unsubscribed) */}
							{hasMapData && (
								<div className="mt-12">
									<CollapsibleSection
										id="location-map"
										title="Property Location Map"
										icon={<MapPinned className="w-5 h-5" />}
										isExpanded={
											expandedSections.locationMap
										}
										onToggle={() =>
											toggleSection("locationMap")
										}
										locked={false}
									>
										<div className="space-y-4">
											<div className="flex items-center justify-between bg-[#f0fdf4] rounded-xl p-4 border border-[#A7FF5E]/30">
												<div className="flex items-center gap-3">
													<div className="w-10 h-10 rounded-lg bg-[#A7FF5E] flex items-center justify-center">
														<MapPin className="w-5 h-5 text-[#16251c]" />
													</div>
													<div>
														<p className="text-xs font-bold text-[#697b8e] uppercase tracking-wider">
															Exact Property
															Location
														</p>
														<p className="text-sm font-semibold text-[#12301b]">
															{lat
																? `${lat.toFixed(6)}° N`
																: "N/A"}
															,{" "}
															{lng
																? `${Math.abs(lng).toFixed(6)}° W`
																: "N/A"}
														</p>
													</div>
												</div>
												<a
													href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
													data-testid="google-maps-link"
													target="_blank"
													rel="noopener noreferrer"
													className="px-4 py-2 bg-white border border-gray-100 rounded-lg text-xs font-bold text-[#12301b] hover:bg-gray-50 transition-colors shadow-sm"
												>
													View on Google Maps
												</a>
											</div>
											<div className="w-full min-h-[60vh] h-[70vh] max-h-[800px] rounded-2xl overflow-hidden border border-gray-100 shadow-inner relative group">
												<div
													ref={mapContainer}
													data-testid="property-map"
													className="w-full h-full"
												/>

												{/* Map Style Toggle */}
												<div className="absolute top-4 right-4 flex flex-col gap-2">
													<button
														onClick={() =>
															setMapStyle(
																"streets"
															)
														}
														data-testid="map-style-streets-button"
														className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-md ${
															mapStyle ===
															"streets"
																? "bg-[#16251c] text-white"
																: "bg-white text-[#16251c] hover:bg-gray-50"
														}`}
													>
														Streets
													</button>
													<button
														onClick={() =>
															setMapStyle(
																"satellite"
															)
														}
														data-testid="map-style-satellite-button"
														className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-md ${
															mapStyle ===
															"satellite"
																? "bg-[#16251c] text-white"
																: "bg-white text-[#16251c] hover:bg-gray-50"
														}`}
													>
														Satellite
													</button>
												</div>
											</div>
										</div>
									</CollapsibleSection>
								</div>
							)}
						</div>

						{/* Right Column - Upgrade Card */}
						{!isSubscribed && (
							<div
								className="w-full lg:w-96 flex-shrink-0"
								style={{ marginTop: "1.8rem" }}
							>
								<div className="sticky top-24">
									<UpgradeCard
										onUpgrade={handleUpgrade}
										isUpgrading={isUpgrading}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Request Photos Modal */}
			<RequestPhotosModal
				isOpen={isRequestPhotosModalOpen}
				onClose={() => setIsRequestPhotosModalOpen(false)}
				properties={
					property
						? [
								{
									id: String(property.id),
									address: String(property.address),
									city: String(property.city ?? ""),
									state: String(property.state ?? ""),
									county: String(property.county ?? ""),
									zip: String(property.zip ?? ""),
									latitude: Number(property.latitude),
									longitude: Number(property.longitude),
								},
							]
						: []
				}
			/>
		</>
	);
}

// Helper Components (Exactly as per Figma Reference)
function StatCard({
	icon,
	label,
	value,
}: {
	icon: React.ReactNode;
	label: string;
	value: string;
}) {
	const getTestId = (label: string) => {
		switch (label) {
			case "Property Type":
				return "property-type-value";
			case "Lot Size":
				return "property-lot-size-value";
			case "Assessed Value":
				return "property-assessed-value";
			case "Tax Status":
				return "property-tax-status-value";
			default:
				return "property-stat-value";
		}
	};

	return (
		<div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all">
			<div className="flex items-center gap-2 text-[#A7FF5E] mb-2">
				{icon}
				<span className="text-xs font-medium text-white/60 uppercase tracking-wider">
					{label}
				</span>
			</div>
			<p
				data-testid={getTestId(label)}
				className="text-xl font-bold text-white"
			>
				{value}
			</p>
		</div>
	);
}

function NavButton({
	id,
	label,
	active,
}: {
	id: string;
	label: string;
	active: boolean;
}) {
	const handleClick = () => {
		const element = document.getElementById(id);
		if (element) {
			const headerOffset = 100;
			const elementPosition = element.getBoundingClientRect().top;
			const offsetPosition =
				elementPosition + window.pageYOffset - headerOffset;

			window.scrollTo({
				top: offsetPosition,
				behavior: "smooth",
			});
		}
	};

	return (
		<button
			onClick={handleClick}
			className={`w-full text-left px-4 py-2 text-sm transition-all relative flex items-center ${
				active
					? "text-[#2769AF] font-bold"
					: "text-[#697b8e] hover:text-[#112133]"
			}`}
		>
			{active && (
				<div className="absolute left-0 w-1 h-4 bg-[#2769AF] rounded-full" />
			)}
			<span
				className={active ? "translate-x-2 transition-transform" : ""}
			>
				{label}
			</span>
		</button>
	);
}

function CollapsibleSection({
	title,
	icon,
	isExpanded,
	onToggle,
	locked,
	premiumBadge,
	children,
	id,
	headerAction,
	lockedTrialCta,
}: {
	title: string;
	icon: React.ReactNode;
	isExpanded: boolean;
	onToggle: () => void;
	locked?: boolean;
	premiumBadge?: boolean;
	children: React.ReactNode;
	id?: string;
	headerAction?: React.ReactNode;
	/** When locked, use this CTA instead of default "14-Day Free Trial" link (e.g. button for checkout when user is signed in) */
	lockedTrialCta?: React.ReactNode;
}) {
	return (
		<div
			id={id}
			data-testid={id ? `${id}-section` : undefined}
			className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all"
		>
			<div
				data-testid="section-header"
				role="button"
				tabIndex={0}
				onClick={onToggle}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onToggle();
					}
				}}
				className="w-full px-6 py-5 bg-gradient-to-r from-white to-gray-50 flex items-center justify-between gap-3 text-left cursor-pointer hover:opacity-95 transition-opacity"
			>
				<div className="flex items-center gap-3 flex-1 min-w-0">
					<div className="w-10 h-10 rounded-xl bg-[#ecf7e4] flex items-center justify-center text-[#417714] flex-shrink-0">
						{icon}
					</div>
					<h3 className="font-bold text-[#112133] truncate">
						{title}
					</h3>
					{premiumBadge && (
						<span className="px-3 py-1 bg-gradient-to-r from-[#A7FF5E] to-[#cbf7a6] text-[#16251c] text-xs font-bold rounded-full uppercase tracking-wide flex-shrink-0">
							Pro
						</span>
					)}
				</div>
				{headerAction && (
					<div
						onClick={(e) => e.stopPropagation()}
						className="flex-shrink-0"
					>
						{headerAction}
					</div>
				)}
				<div className="flex-shrink-0">
					{isExpanded ? (
						<ChevronUp className="w-5 h-5 text-gray-400" />
					) : (
						<ChevronDown className="w-5 h-5 text-gray-400" />
					)}
				</div>
			</div>

			{isExpanded && (
				<div className="relative">
					<div className={`p-6 ${locked ? "min-h-[300px]" : ""}`}>
						{children}
					</div>

					{/* Lock Overlay */}
					{locked && (
						<div
							data-testid="locked-overlay"
							className="absolute inset-0 backdrop-blur-md bg-gradient-to-br from-black/60 to-black/40 flex items-center justify-center rounded-b-2xl min-h-[300px]"
							onClick={(e) => e.stopPropagation()}
							role="presentation"
						>
							<div className="text-center px-8 py-8">
								<div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#A7FF5E] to-[#cbf7a6] flex items-center justify-center mx-auto mb-4 shadow-2xl">
									<Lock
										data-testid="lock-icon"
										className="w-10 h-10 text-[#16251c]"
									/>
								</div>
								<h4
									data-testid="premium-data-locked"
									className="text-white font-bold text-lg mb-2"
								>
									Premium Data Locked
								</h4>
								<p className="text-white/80 text-sm mb-4">
									Unlock detailed parcel information with Tax
									Tax Lien Vest Pro
								</p>
								<div className="flex items-center justify-center gap-2 text-[#A7FF5E] text-xs font-semibold">
									<Shield className="w-4 h-4 flex-shrink-0" />
									{lockedTrialCta ?? (
										<Link
											href="/signup"
											className="underline hover:text-[#cbf7a6] focus:outline-none focus:ring-2 focus:ring-[#A7FF5E] focus:ring-offset-2 focus:ring-offset-transparent rounded cursor-pointer"
											onClick={(e) => e.stopPropagation()}
										>
											14-Day Free Trial Available
										</Link>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

function InfoCard({
	label,
	value,
	icon,
	fullWidth,
	blurred,
	courtCaseUrl,
	courtCaseNumber,
	zillowUrl,
	realtorUrl,
	onCopy,
	copiedCardKey,
}: {
	label: string;
	value: string;
	icon: React.ReactNode;
	fullWidth?: boolean;
	blurred?: boolean;
	courtCaseUrl?: string | null;
	courtCaseNumber?: string;
	zillowUrl?: string | null;
	realtorUrl?: string | null;
	onCopy?: (text: string, key: string) => void;
	copiedCardKey?: string | null;
}) {
	const cardKey = `info-card-${label}`;
	const isCopied = copiedCardKey === cardKey;
	const showCopy =
		value !== "N/A" && value.trim() !== "" && onCopy && !blurred;
	const handleCourtCaseClick = async (
		e: React.MouseEvent<HTMLAnchorElement>
	) => {
		e.preventDefault();
		if (!courtCaseNumber || !courtCaseUrl) return;

		try {
			// Copy to clipboard
			await navigator.clipboard.writeText(courtCaseNumber);

			// Show prominent notification
			toast.success("Court case number copied!", {
				description: `"${courtCaseNumber}" has been copied to your clipboard. Redirecting to court portal...`,
				duration: 3000,
				action: {
					label: "Open",
					onClick: () => window.open(courtCaseUrl, "_blank"),
				},
			});

			// Small delay to show the toast, then navigate
			setTimeout(() => {
				window.open(courtCaseUrl, "_blank");
			}, 800);
		} catch (error) {
			console.error("Failed to copy to clipboard:", error);
			toast.error("Failed to copy court case number", {
				description: "Redirecting to court portal anyway...",
				duration: 3000,
			});
			// Still navigate even if copy fails
			setTimeout(() => {
				if (courtCaseUrl) {
					window.open(courtCaseUrl, "_blank");
				}
			}, 800);
		}
	};

	return (
		<div
			data-testid={`info-card-${label.toLowerCase().replace(/\s+/g, "-")}`}
			className={`${fullWidth ? "col-span-2" : ""} bg-gray-50 rounded-xl p-4 border border-gray-100 hover:border-[#A7FF5E]/30 transition-all`}
		>
			<div className="flex items-center gap-2 text-[#697b8e] mb-2">
				{icon}
				<span className="text-xs font-medium uppercase tracking-wide">
					{label}
				</span>
			</div>
			<div className="flex items-center gap-2 flex-wrap">
				<p
					className={`text-base font-semibold text-[#12301b] ${blurred ? "blur-sm select-none" : ""}`}
				>
					{value}
				</p>
				{showCopy && (
					<button
						type="button"
						onClick={() => onCopy(value, cardKey)}
						className="p-1 rounded text-gray-500 hover:text-[#417714] transition-colors shrink-0"
						aria-label={`Copy ${label}`}
					>
						{isCopied ? (
							<Check className="w-4 h-4" />
						) : (
							<Copy className="w-4 h-4" />
						)}
					</button>
				)}
			</div>
			{label === "Court Case" &&
				courtCaseUrl &&
				courtCaseNumber &&
				courtCaseNumber !== "N/A" && (
					<a
						href={courtCaseUrl}
						onClick={handleCourtCaseClick}
						className="mt-2 inline-flex items-center gap-1 text-sm text-[#417714] hover:text-[#16251c] font-medium transition-colors"
					>
						<ArrowRight className="w-3 h-3" />
						Go to court case
					</a>
				)}
			{label === "Zillow Web Link" &&
				zillowUrl &&
				value !== "N/A" &&
				value && (
					<a
						href={zillowUrl}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => {
							// Additional validation on click for professional reliability
							try {
								const validatedUrl = new URL(zillowUrl);
								// URL is valid, allow navigation
							} catch (error) {
								e.preventDefault();
								console.error(
									"Invalid Zillow URL:",
									zillowUrl,
									error
								);
								toast.error("Invalid URL format", {
									description:
										"The Zillow link appears to be malformed.",
									duration: 3000,
								});
							}
						}}
						className="mt-2 inline-flex items-center gap-1 text-sm text-[#417714] hover:text-[#16251c] font-medium transition-colors"
						title={`Open Zillow property page: ${zillowUrl}`}
					>
						<ArrowRight className="w-3 h-3" />
						Go to Zillow
					</a>
				)}
			{label === "Realtor Web Link" &&
				realtorUrl &&
				value !== "N/A" &&
				value && (
					<a
						href={realtorUrl}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => {
							// Additional validation on click for professional reliability
							try {
								const validatedUrl = new URL(realtorUrl);
								// URL is valid, allow navigation
							} catch (error) {
								e.preventDefault();
								console.error(
									"Invalid Realtor URL:",
									realtorUrl,
									error
								);
								toast.error("Invalid URL format", {
									description:
										"The Realtor link appears to be malformed.",
									duration: 3000,
								});
							}
						}}
						className="mt-2 inline-flex items-center gap-1 text-sm text-[#417714] hover:text-[#16251c] font-medium transition-colors"
						title={`Open Realtor property page: ${realtorUrl}`}
					>
						<ArrowRight className="w-3 h-3" />
						Go to Realtor
					</a>
				)}
		</div>
	);
}

function UpgradeCard({
	onUpgrade,
	isUpgrading,
}: {
	onUpgrade: () => void;
	isUpgrading?: boolean;
}) {
	return (
		<div className="bg-gradient-to-br from-[#16251c] to-[#0f1b13] rounded-2xl overflow-hidden shadow-2xl border border-[#A7FF5E]/20">
			{/* Header */}
			<div className="p-6 pb-4 relative overflow-hidden">
				<div className="absolute -top-20 -right-20 w-48 h-48 bg-[#A7FF5E]/10 rounded-full blur-3xl" />
				<div className="absolute -bottom-16 -left-16 w-32 h-32 bg-[#A7FF5E]/10 rounded-full blur-3xl" />

				<div className="relative">
					<div className="flex items-center gap-3 mb-4">
						<div className="w-14 h-14 bg-[#ecf7e4] rounded-xl flex items-center justify-center">
							<Building className="w-8 h-8 text-[#417714]" />
						</div>
						<div>
							<h3 className="text-xl font-bold text-white uppercase">
								Tax Lien Vest Pro
							</h3>
							<p className="text-xs text-white/60">
								Premium Property Intelligence
							</p>
						</div>
					</div>

					<div className="flex items-end gap-3 mb-2">
						<span className="text-4xl font-black text-white">
							$49
						</span>
						<span className="text-lg text-white/80 mb-1">
							/month
						</span>
						<span className="text-sm text-white/50 line-through mb-1">
							$299
						</span>
					</div>
					<p className="text-sm text-white/70">
						Save 83% with annual billing
					</p>
				</div>
			</div>

			{/* Features */}
			<div className="bg-white p-6 space-y-4">
				<div className="flex items-center gap-2 mb-4">
					<div className="w-2 h-2 rounded-full bg-[#A7FF5E] animate-pulse" />
					<span className="text-xs font-bold text-[#12301b] uppercase tracking-wider">
						Unlock Premium Features
					</span>
				</div>

				<UpgradeFeature
					icon={<Database className="w-5 h-5" />}
					text="Complete Parcel Data & Analytics"
					highlight
				/>
				<UpgradeFeature
					icon={<MapPinned className="w-5 h-5" />}
					text="Geospatial Coordinates & Mapping"
					highlight
				/>
				<UpgradeFeature
					icon={<TrendingUp className="w-5 h-5" />}
					text="Advanced Property Valuations"
				/>
				<UpgradeFeature
					icon={<Shield className="w-5 h-5" />}
					text="Legal Case & Title Information"
				/>
				<UpgradeFeature
					icon={<Download className="w-5 h-5" />}
					text="Export to CSV, Excel & Google Maps"
				/>
				<UpgradeFeature
					icon={<Clock className="w-5 h-5" />}
					text="Real-Time Tax Lien Updates"
				/>

				<div className="pt-4 border-t border-gray-100">
					<div className="flex items-start gap-2 bg-[#f0f9ff] rounded-lg p-3 mb-4">
						<Building className="w-4 h-4 text-[#2769AF] mt-0.5" />
						<div>
							<p className="text-xs font-semibold text-[#2769AF] mb-1">
								Active in Your Area
							</p>
							<p className="text-xs text-[#697b8e]">
								1,847 properties with owner data ready to export
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* CTA */}
			<div className="p-6 bg-gradient-to-br from-[#16251c] to-[#0f1b13]">
				<button
					data-testid="start-trial-button"
					onClick={onUpgrade}
					disabled={isUpgrading}
					className="w-full bg-gradient-to-r from-[#A7FF5E] to-[#cbf7a6] text-[#16251c] py-4 px-6 rounded-xl font-bold text-base hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 mb-3 disabled:opacity-70 disabled:cursor-not-allowed"
				>
					{isUpgrading ? (
						<Loader2 className="w-5 h-5 animate-spin" />
					) : (
						<Sparkles className="w-5 h-5" />
					)}
					{isUpgrading ? "Redirecting..." : "Start 14-Day Free Trial"}
					{!isUpgrading && <ArrowRight className="w-5 h-5" />}
				</button>

				<div className="flex items-center justify-center gap-2 text-white/70 text-xs">
					<CheckCircle className="w-4 h-4 text-[#A7FF5E]" />
					<span>No credit card required • Cancel anytime</span>
				</div>
			</div>
		</div>
	);
}

function UpgradeFeature({
	icon,
	text,
	highlight,
}: {
	icon: React.ReactNode;
	text: string;
	highlight?: boolean;
}) {
	return (
		<div
			className={`flex items-center gap-3 ${highlight ? "bg-[#f0fdf4] -mx-2 px-2 py-2 rounded-lg" : ""}`}
		>
			<div
				className={`flex-shrink-0 ${highlight ? "text-[#417714]" : "text-[#12301b]"}`}
			>
				{icon}
			</div>
			<span
				className={`text-sm ${highlight ? "font-semibold text-[#12301b]" : "text-[#697b8e]"}`}
			>
				{text}
			</span>
			{highlight && (
				<CheckCircle className="w-4 h-4 text-[#417714] ml-auto" />
			)}
		</div>
	);
}

// Photos Section Component
function PhotosSection({
	property,
	setIsRequestPhotosModalOpen,
}: {
	property: Record<string, unknown> | null;
	setIsRequestPhotosModalOpen: (open: boolean) => void;
}) {
	const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	const [checkingPhotoRequest, setCheckingPhotoRequest] = useState(false);
	const [requestAlreadySent, setRequestAlreadySent] = useState<
		boolean | null
	>(null);
	const [checkingRequest, setCheckingRequest] = useState(false);

	console.log("--------------------------------");
	console.log(property);
	console.log("--------------------------------");
	// Get photos from property.medias
	const photos = property?.medias as string[] | undefined;
	const hasPhotos = photos && Array.isArray(photos) && photos.length > 0;

	// Check once on mount when there are no photos (property details page open)
	useEffect(() => {
		if (hasPhotos || !property?.id) return;
		let cancelled = false;
		setCheckingRequest(true);
		checkPropertyMediaRequest(String(property.id))
			.then((res) => {
				if (!cancelled) setRequestAlreadySent(!res.canRequest);
			})
			.catch(() => {
				if (!cancelled) setRequestAlreadySent(null);
			})
			.finally(() => {
				if (!cancelled) setCheckingRequest(false);
			});
		return () => {
			cancelled = true;
		};
	}, [property?.id, hasPhotos]);

	const openPhotoViewer = (photoUrl: string) => {
		setSelectedPhoto(photoUrl);
		setIsViewerOpen(true);
	};

	const closePhotoViewer = () => {
		setIsViewerOpen(false);
		setSelectedPhoto(null);
	};

	const handleRequestPhotosClick = async () => {
		if (!property?.id) {
			toast.error("Property identifier not available.");
			return;
		}
		setCheckingPhotoRequest(true);
		try {
			const res = await checkPropertyMediaRequest(String(property.id));
			if (!res.canRequest) {
				toast.info("Request already initiated for this property.");
				return;
			}
			setIsRequestPhotosModalOpen(true);
		} catch (e) {
			toast.error(
				e instanceof Error ? e.message : "Failed to check request."
			);
		} finally {
			setCheckingPhotoRequest(false);
		}
	};

	if (!hasPhotos) {
		if (checkingRequest) {
			return (
				<div className="space-y-6">
					<div className="text-center py-12">
						<Loader2
							className="w-10 h-10 animate-spin text-[#417714] mx-auto mb-4"
							aria-hidden
						/>
						<p className="text-sm text-gray-600">
							Checking request status…
						</p>
					</div>
				</div>
			);
		}
		if (requestAlreadySent === true) {
			return (
				<div className="space-y-6">
					<PhotoRequestAlreadySentBlock
						address={String(property?.address ?? "")}
					/>
				</div>
			);
		}
		return (
			<div className="space-y-6">
				<div className="text-center py-8">
					<div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
						<Camera className="w-8 h-8 text-gray-400" />
					</div>
					<h3 className="text-lg font-semibold text-gray-900 mb-2">
						No Photos Available
					</h3>
					<p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
						Request professional ground-level photographs of this
						property to get a better understanding of its condition
						and surroundings.
					</p>
					<button
						onClick={handleRequestPhotosClick}
						disabled={checkingPhotoRequest}
						className="inline-flex items-center gap-2 bg-[#A7FF5E] hover:bg-[#96ee4d] text-[#16251c] px-6 py-3 rounded-lg font-semibold transition-colors shadow-sm disabled:opacity-70 disabled:pointer-events-none"
					>
						{checkingPhotoRequest ? (
							<>
								<Loader2
									className="w-5 h-5 animate-spin"
									aria-hidden
								/>
								Checking…
							</>
						) : (
							<>
								<Camera className="w-5 h-5" />
								Request Property Photos
							</>
						)}
					</button>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<p className="text-sm text-gray-600">
						{photos.length} photo{photos.length !== 1 ? "s" : ""}{" "}
						available
					</p>
				</div>

				{/* Masonry Photo Grid */}
				<div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
					{photos.map((photoUrl, index) => (
						<div
							key={index}
							className="break-inside-avoid mb-4 group cursor-pointer"
							onClick={() => openPhotoViewer(photoUrl)}
						>
							<div className="relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02]">
								<img
									src={MEDIA_BASE_URL + photoUrl}
									alt={`Property photo ${index + 1}`}
									className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
									loading="lazy"
									onError={(e) => {
										const target =
											e.target as HTMLImageElement;
										target.style.display = "none";
										// Show error placeholder
										const parent = target.parentElement;
										if (parent) {
											parent.innerHTML = `
												<div class="w-full h-48 bg-gray-100 flex items-center justify-center">
													<div class="text-center">
														<svg class="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
														</svg>
														<p class="text-xs text-gray-500">Image failed to load</p>
													</div>
												</div>
											`;
										}
									}}
								/>
								{/* Hover overlay */}
								<div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
									<div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
										<div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
											<svg
												className="w-6 h-6 text-gray-700"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
												/>
											</svg>
										</div>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Photo Viewer Modal */}
			{isViewerOpen && selectedPhoto && (
				<PhotoViewer
					photoUrl={selectedPhoto}
					allPhotos={photos}
					currentIndex={photos.indexOf(selectedPhoto)}
					onClose={closePhotoViewer}
					onNavigate={(newIndex) => {
						if (newIndex >= 0 && newIndex < photos.length) {
							setSelectedPhoto(photos[newIndex]);
						}
					}}
				/>
			)}
		</>
	);
}

// Photo Viewer Modal Component
function PhotoViewer({
	photoUrl,
	allPhotos,
	currentIndex,
	onClose,
	onNavigate,
}: {
	photoUrl: string;
	allPhotos: string[];
	currentIndex: number;
	onClose: () => void;
	onNavigate: (index: number) => void;
}) {
	const [scale, setScale] = useState(1);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const imageRef = useRef<HTMLImageElement>(null);

	// Reset zoom and position when photo changes
	useEffect(() => {
		setScale(1);
		setPosition({ x: 0, y: 0 });
	}, [photoUrl]);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			switch (e.key) {
				case "Escape":
					onClose();
					break;
				case "ArrowLeft":
					if (currentIndex > 0) {
						onNavigate(currentIndex - 1);
					}
					break;
				case "ArrowRight":
					if (currentIndex < allPhotos.length - 1) {
						onNavigate(currentIndex + 1);
					}
					break;
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [currentIndex, allPhotos.length, onClose, onNavigate]);

	const handleZoomIn = () => {
		setScale((prev) => Math.min(prev * 1.5, 5));
	};

	const handleZoomOut = () => {
		setScale((prev) => Math.max(prev / 1.5, 0.5));
	};

	const handleResetZoom = () => {
		setScale(1);
		setPosition({ x: 0, y: 0 });
	};

	const handleMouseDown = (e: React.MouseEvent) => {
		if (scale > 1) {
			setIsDragging(true);
			setDragStart({
				x: e.clientX - position.x,
				y: e.clientY - position.y,
			});
		}
	};

	const handleMouseMove = (e: React.MouseEvent) => {
		if (isDragging && scale > 1) {
			setPosition({
				x: e.clientX - dragStart.x,
				y: e.clientY - dragStart.y,
			});
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleWheel = (e: React.WheelEvent) => {
		e.preventDefault();
		const delta = e.deltaY > 0 ? 0.9 : 1.1;
		setScale((prev) => Math.min(Math.max(prev * delta, 0.5), 5));
	};

	return (
		<div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
			{/* Header */}
			<div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-4">
				<div className="flex items-center justify-between text-white">
					<div className="flex items-center gap-4">
						<h3 className="text-lg font-semibold">
							Photo {currentIndex + 1} of {allPhotos.length}
						</h3>
						<div className="text-sm text-white/70">
							{Math.round(scale * 100)}% zoom
						</div>
					</div>
					<button
						onClick={onClose}
						className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
					>
						<svg
							className="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>
			</div>

			{/* Navigation Arrows */}
			{currentIndex > 0 && (
				<button
					onClick={() => onNavigate(currentIndex - 1)}
					className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
				>
					<svg
						className="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
				</button>
			)}

			{currentIndex < allPhotos.length - 1 && (
				<button
					onClick={() => onNavigate(currentIndex + 1)}
					className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
				>
					<svg
						className="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M9 5l7 7-7 7"
						/>
					</svg>
				</button>
			)}

			{/* Zoom Controls */}
			<div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-black/50 rounded-full px-4 py-2">
				<button
					onClick={handleZoomOut}
					className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
					disabled={scale <= 0.5}
				>
					<svg
						className="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M20 12H4"
						/>
					</svg>
				</button>
				<button
					onClick={handleResetZoom}
					className="px-3 py-1 text-sm text-white hover:bg-white/10 rounded transition-colors"
				>
					Reset
				</button>
				<button
					onClick={handleZoomIn}
					className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
					disabled={scale >= 5}
				>
					<svg
						className="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M12 4v16m8-8H4"
						/>
					</svg>
				</button>
			</div>

			{/* Image Container */}
			<div
				className="flex-1 flex items-center justify-center p-4 "
				onClick={(e) => {
					if (e.target === e.currentTarget) {
						onClose();
					}
				}}
			>
				<Image
					fill
					ref={imageRef}
					src={MEDIA_BASE_URL + photoUrl}
					alt={`Property photo ${currentIndex + 1}`}
					className={`max-w-full max-h-full object-contain transition-transform duration-200 ${
						scale > 1 ? "cursor-grab" : "cursor-zoom-in"
					} ${isDragging ? "cursor-grabbing" : ""}`}
					style={{
						transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
					}}
					onMouseDown={handleMouseDown}
					onMouseMove={handleMouseMove}
					onMouseUp={handleMouseUp}
					onMouseLeave={handleMouseUp}
					onWheel={handleWheel}
					onClick={(e) => {
						e.stopPropagation();
						if (scale === 1) {
							handleZoomIn();
						}
					}}
					draggable={false}
				/>
			</div>

			{/* Instructions */}
			<div className="absolute bottom-4 right-4 z-10 text-white/70 text-sm space-y-1">
				<div>Click image to zoom</div>
				<div>Scroll to zoom</div>
				<div>Drag to pan when zoomed</div>
				<div>ESC to close</div>
			</div>
		</div>
	);
}
